import React from 'react';

import { Close } from '@mui/icons-material';
import { IconButton, TableCell, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import moment from 'moment-timezone';
import { type TFunction, useTranslation } from 'react-i18next';

import { type ClassSession, type ClassSessionWithGroupData } from './types';

const StyledTableRow = styled(TableRow)`
    padding: 0;

    td {
        background: ${(props) => props.theme.palette.background.default};
    }

    &:hover {
        td {
            background: ${(props) => props.theme.palette.action.hover};

            button {
                opacity: 1;
            }
        }
    }
`;

const StyledTableCell = styled(TableCell)`
    border: 0;
`;

const StyledTableCellClose = styled(StyledTableCell)`
    width: 0;
    padding: 0;

    button {
        opacity: 0;
    }
`;

interface DaysOfWeekInterface {
    startTime: number;
    endTime: number;
    timezone: string;
    t: TFunction<'calender'>;
}

const returnDaysOfTheWeek = ({ startTime, endTime, timezone, t }: DaysOfWeekInterface): string => {
    const startUtcDate = new Date(startTime * 1000);
    const endUtcDate = new Date(endTime * 1000);

    const startingDayLocalDate = utcToZonedTime(startUtcDate, timezone);
    const endingDayLocalDate = utcToZonedTime(endUtcDate, timezone);

    // Get the day of the week as a number (0 for Sunday, 1 for Monday, etc.)
    const startDayOfWeek: number = getDay(startingDayLocalDate);
    const endDayOfWeek: number = getDay(endingDayLocalDate);

    const startingDay = t(`days.${startDayOfWeek}`);
    const endingDay = t(`days.${endDayOfWeek}`);

    // shows a range for overnight classes
    const dayOfTheWeek = startingDay === endingDay ? startingDay : `${startingDay} - ${endingDay}`;

    return dayOfTheWeek;
};

interface TimeOfDayRangeInterface {
    session: ClassSession;
}

const returnTimeOfDayRange = ({ session }: TimeOfDayRangeInterface) => {
    if (session.timezone) {
        return `${moment.unix(session.start_time).tz(session.timezone).format('h:mm A')} - ${moment
            .unix(session.end_time)
            .tz(session.timezone)
            .format('h:mm A')}`;
    }

    return `${moment.unix(session.start_time).format('h:mm A')} - ${moment.unix(session.end_time).format('h:mm A')}`;
};

const returnStartDate = ({ session }: TimeOfDayRangeInterface) => {
    if (session.timezone) {
        return moment.unix(session.start_time).tz(session.timezone).format('L');
    }

    return moment.unix(session.start_time).format('L');
};

interface SessionRowInterface {
    session: ClassSessionWithGroupData;
    sessionsLength: number;
    excludeSession?: (session: ClassSessionWithGroupData) => void;
}

const SessionRow: React.FC<SessionRowInterface> = ({ session, sessionsLength, excludeSession }) => {
    const { t } = useTranslation('calendar');
    const daysOfTheWeek = returnDaysOfTheWeek({
        startTime: session.start_time,
        endTime: session.end_time,
        timezone: session.timezone,
        t
    });
    const timeOfDayRange = returnTimeOfDayRange({ session });
    const startDate = returnStartDate({ session });
    const { label } = session;

    return (
        <StyledTableRow>
            <StyledTableCell>{daysOfTheWeek}</StyledTableCell>
            <StyledTableCell>{startDate}</StyledTableCell>
            <StyledTableCell>{timeOfDayRange}</StyledTableCell>
            <StyledTableCell>{label}</StyledTableCell>
            {excludeSession && sessionsLength > 1 ? (
                <StyledTableCellClose>
                    <IconButton onClick={() => excludeSession(session)} size="small">
                        <Close />
                    </IconButton>
                </StyledTableCellClose>
            ) : null}
        </StyledTableRow>
    );
};

export default SessionRow;
